import { Inject, Injectable, OnDestroy, Pipe, PipeTransform } from '@angular/core';
import { Intl as TemporalIntl, Temporal } from '@js-temporal/polyfill';
import { TranslateService } from '@ngx-translate/core';
import { DateTimeFormatOptions } from '../../env';
import { Subscription } from 'rxjs';
import '../../../../../intl.d';
import { AppStartupService } from '../services';

const formatterCache = new Map<string, TemporalIntl.DateTimeFormat>();

@Injectable()
abstract class DateTimePipeBase implements OnDestroy {
    protected formatter!: TemporalIntl.DateTimeFormat;
    protected options: DateTimeFormatOptions = {};
    protected langChangeSubscription?: Subscription;

    constructor(
        @Inject('ENV') public env: any,
        protected translate: TranslateService,
        protected appStartupService: AppStartupService
    ) {}

    ngOnDestroy(): void {
        this.langChangeSubscription?.unsubscribe();
    }

    protected ensureLatestFormatter(options?: DateTimeFormatOptions): void {
        this.langChangeSubscription ??= this.translate.onLangChange.subscribe(() => {
            this.updateFormatter();
        });

        this.options = {
            timeZone: this.appStartupService.getTimezone(),
            ...options
        };

        this.updateFormatter();
    }

    protected updateFormatter(): void {
        const { locale, ...options } = this.options;

        let selectedLocale =
            this.normalizeLocale(locale) ?? this.normalizeLocale(this.translate.currentLang) ?? 'en-US';

        try {
            selectedLocale = new Intl.Locale(selectedLocale).maximize().toString();
        } catch {
            selectedLocale = typeof navigator !== 'undefined' && navigator.language ? navigator.language : 'en-US';
        }

        const newFormatter = this.getCachedFormatter(selectedLocale, options);
        if (newFormatter !== this.formatter) {
            this.formatter = newFormatter;
        }
    }

    protected normalizeLocale(langTag?: string): string | undefined {
        if (!langTag) return langTag;
        const map: Record<string, string> = {
            se: 'sv-SE',
            sv: 'sv-SE',
            en: 'en-US',
            lv: 'lv-LV',
            ru: 'ru-RU',
            et: 'et-EE',
            lt: 'lt-LT'
        };
        return map[langTag] ?? langTag;
    }

    private getCachedFormatter(
        locales?: string | string[] | undefined,
        options?: DateTimeFormatOptions
    ): TemporalIntl.DateTimeFormat {
        const key = JSON.stringify([locales, options]);
        let cached = formatterCache.get(key);
        if (!cached) {
            cached = new TemporalIntl.DateTimeFormat(locales, options);
            formatterCache.set(key, cached);
        }
        return cached;
    }

    protected formatWithFixedPattern(date: TemporalIntl.Formattable, pattern: string): string {
        const tz = this.appStartupService.getTimezone();
        const locale = this.formatter.resolvedOptions().locale;

        let zdt: Temporal.ZonedDateTime;
        try {
            zdt = Temporal.ZonedDateTime.from(date as any).withTimeZone(tz);
        } catch (e) {
            zdt = Temporal.Instant.from((date instanceof Date ? date.toISOString() : date) as any).toZonedDateTimeISO(
                tz
            );
        }

        const weekday = zdt.toLocaleString(locale, {
            weekday: 'short'
        });

        const { year: y, month: M, day: d, hour: H, minute: m } = zdt;

        const formattedDate = pattern
            .replace(/yyyy/g, String(y))
            .replace(/MM/g, String(M).padStart(2, '0'))
            .replace(/dd/g, String(d).padStart(2, '0'))
            .replace(/HH/g, String(H).padStart(2, '0'))
            .replace(/mm/g, String(m).padStart(2, '0'));

        return `${weekday}, ${formattedDate}`;
    }
}

@Pipe({ name: 'dateTime', pure: false })
export class DateTimePipe extends DateTimePipeBase implements PipeTransform {
    transform(date: TemporalIntl.Formattable, options?: DateTimeFormatOptions): string {
        this.ensureLatestFormatter(options);

        const useFixed = !!this.env?.FORMATTING?.USE_SETTINGS_DATE_FORMAT_FOR_ALL_LANGUAGE;
        if (useFixed) {
            const pattern = this.env?.PERIOD_TICKET_DATE_FORMAT ?? 'yyyy-MM-dd HH:mm';
            return this.formatWithFixedPattern(date, pattern);
        }

        return this.formatter.format(date);
    }
}

@Pipe({ name: 'dateTimeRange', pure: false })
export class DateTimeRangePipe extends DateTimePipeBase implements PipeTransform {
    transform<T extends TemporalIntl.Formattable>(
        [startDate, endDate]: [startDate: T, endDate: T],
        { rangeSeparator, ...options }: DateTimeFormatOptions = {}
    ): string {
        this.ensureLatestFormatter(options);

        const sep = rangeSeparator ?? ' – ';
        const useFixed = !!this.env?.FORMATTING?.USE_SETTINGS_DATE_FORMAT_FOR_ALL_LANGUAGE;

        if (useFixed) {
            const pattern = this.env?.PERIOD_TICKET_DATE_FORMAT ?? 'yyyy-MM-dd HH:mm';
            const startDateFixedPattern = this.formatWithFixedPattern(startDate, pattern);
            const endDateFixedPattern = this.formatWithFixedPattern(endDate, pattern);
            return `${startDateFixedPattern}${sep}${endDateFixedPattern}`;
        }

        if (rangeSeparator) {
            return [startDate, endDate].map((date) => this.formatter.format(date)).join(rangeSeparator);
        }

        return this.formatter.formatRange(startDate, endDate);
    }
}
